/*
 * Early diary start (AB#3228).
 *
 * Most sites open their diary at 08:00. Some, currently only Dagenham, open
 * earlier. Rather than hard code the earlier time, the sites that open early
 * and the time they open at are configured in .env, in the same style as
 * REACT_APP_CHRONOS_TWENTY_MINUTE_DEFAULT_TEAMS:
 *
 *   REACT_APP_CHRONOS_EARLY_START_TEAMS      team IDs of the early sites
 *   REACT_APP_CHRONOS_EARLY_START_APP_TYPES  appointment type IDs of those sites
 *   REACT_APP_CHRONOS_EARLY_START_TIME       the time they open, as HH:MM
 *
 * All times here are HH:MM; FullCalendar treats the seconds in a duration as
 * optional, so there is no need to be more precise than the minute.
 *
 * Both a team list and an appointment type list are needed because the team
 * calendar is selected by team while the appointment type calendar is selected
 * by appointment type; there is no team on that screen to test.
 */

/* The time every other calendar starts the working day at. */
const DEFAULT_START_TIME = '08:00';

const parseIDList = setting => (setting?? '')
    .split(',', 100)
    .map(i => Number.parseInt(i, 10))
    .filter(i => Number.isInteger(i));

/* The configured early start time, falling back to the normal start time if
 * the setting is missing or empty. */
const earlyStartTime = () => {
    const setting = (process.env.REACT_APP_CHRONOS_EARLY_START_TIME?? '').trim();
    return setting.length? setting: DEFAULT_START_TIME;
};

export const isEarlyStartTeam = teamID =>
    parseIDList(process.env.REACT_APP_CHRONOS_EARLY_START_TEAMS).includes(teamID);

export const isEarlyStartAppointmentType = appointmentTypeID =>
    parseIDList(process.env.REACT_APP_CHRONOS_EARLY_START_APP_TYPES)
        .includes(appointmentTypeID);

/* The time of day, as HH:MM, that the given team's calendar starts. */
export const teamDayStartTime = teamID =>
    isEarlyStartTeam(teamID)? earlyStartTime(): DEFAULT_START_TIME;

/* The time of day, as HH:MM, that the given appointment type's calendar
 * starts. */
export const appointmentTypeDayStartTime = appointmentTypeID =>
    isEarlyStartAppointmentType(appointmentTypeID)?
        earlyStartTime(): DEFAULT_START_TIME;

/* The first slot shown for a team. Teams with a non standard grid interval
 * pass their own default, which is a little before 08:00 so that the grid
 * lines land on the hour; early starting teams start at the configured time
 * instead. */
export const teamSlotMinTime = (teamID, defaultSlotMinTime) =>
    isEarlyStartTeam(teamID)? teamDayStartTime(teamID): defaultSlotMinTime;
